import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CirclePauseIcon,
  CirclePlayIcon,
  AudioControlIcon,
  AudioMuteIcon,
  FullscreenIcon,
} from '@rsos/assets/icons';
import AttentionTriangleIcon from '@rsos/assets/icons/src/AttentionTriangleIcon';
import Loader from '@rsos/components/capstone/Loader';
import { Absolute, Box, Button, Text } from '@rsos/components/capstone/base';
import { VideoPlayer, hasAudio } from '@rsos/video';
import {
  trackToggleVideoPause,
  trackToggleVideoPlay,
  trackVideoError,
  trackVideoLoadStart,
  trackVideoLoadedData,
} from './ellingtonCardTrackings';

const VideoContainer = styled.div`
  position: relative;
`;

const Circle = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: red;
`;

const Live = styled(Absolute)`
  bottom: 4px;
  left: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconButton = styled(Button).attrs({
  btnType: 'invisible',
})`
  color: ${({ theme, color }) =>
    theme.colors[color] || theme.colors.secondaryLink};
  ${({ height }) => `height: ${height};`}

  circle {
    color: ${({ theme }) => theme.colors.secondaryText};
    fill-opacity: 0.3;
  }

  &:hover {
    background: transparent;

    color: ${({ theme, color }) =>
      theme.colors[color] || theme.colors.primaryText};

    circle {
      fill-opacity: 0.5;
    }
  }
`;

const CustomAbsolute = styled(Absolute).attrs({
  top: 0,
  left: 0,
  width: '100%',
})`
  height: 100%;
`;

const LoaderBackground = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 5px;
  padding: 8px;

  > div {
    width: auto;
    height: auto;
  }
`;

const CustomVideo = ({ ownData, irpVersion }) => {
  const { source = '', format = '', token = '', id = '' } = ownData;
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasSound = hasAudio(videoRef?.current);

  const togglePlaying = () => {
    if (isPlaying) {
      videoRef.current.pause();

      trackToggleVideoPause(id, irpVersion);
    } else {
      videoRef.current.currentTime = videoRef.current.duration;
      videoRef.current.play();

      trackToggleVideoPlay(id, irpVersion);
    }
  };

  const toggleMuted = () => {
    if (isMuted && hasSound) {
      videoRef.current.muted = false;
      setIsMuted(false);
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current.mozRequestFullScreen) {
      videoRef.current.mozRequestFullScreen();
    } else if (videoRef.current.webkitRequestFullScreen) {
      videoRef.current.webkitRequestFullScreen();
    }
  };

  const handleFatalError = () => {
    setHasError(true);
    setIsLoading(false);
    trackVideoError(id, irpVersion);
  };

  useEffect(() => {
    const current = videoRef.current;
    const setPlayingFalse = () => setIsPlaying(false);
    const setPlayingTrue = () => setIsPlaying(true);
    const setLoadingFalse = () => setIsLoading(false);
    const setLoadingTrue = () => setIsLoading(true);

    const handleLoadStart = () => {
      trackVideoLoadStart(id, irpVersion);
      if (!hasError) {
        setLoadingTrue();
      }
    };

    const handleLoadedData = () => {
      trackVideoLoadedData(id, irpVersion);
      setLoadingFalse();
    };

    // Video reached end of livestream, and waiting on next 6-8 second batch
    current.addEventListener('waiting', setLoadingTrue);
    current.addEventListener('canplay', setLoadingFalse);
    current.addEventListener('pause', setPlayingFalse);
    // when the user agent can play the media
    current.addEventListener('play', setPlayingTrue);

    // Video makes initial api call
    current.addEventListener('loadstart', handleLoadStart);
    current.addEventListener('loadeddata', handleLoadedData);

    return () => {
      current.removeEventListener('waiting', setLoadingTrue);
      current.removeEventListener('canplay', setLoadingFalse);
      current.removeEventListener('pause', setPlayingFalse);
      current.removeEventListener('play', setPlayingTrue);
      current.removeEventListener('loadstart', handleLoadStart);
      current.removeEventListener('loadeddata', handleLoadedData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return (
    <Box p="12px 20px 0px">
      <VideoContainer ref={containerRef}>
        <VideoPlayer
          playerId={`video-${source}`}
          playerRef={videoRef}
          src={source}
          authToken={token}
          format={format}
          muted={isMuted}
          onFatalError={handleFatalError}
        />
        {isLoading && (
          <CustomAbsolute>
            <Box justify="center" align="center" height="100%">
              <LoaderBackground>
                <Loader noText />
              </LoaderBackground>
            </Box>
          </CustomAbsolute>
        )}
        {hasError && (
          <CustomAbsolute>
            <Box
              bg="lightDanger"
              p="12px 16px"
              horizontal
              align="center"
              borderRadius="5px"
            >
              <AttentionTriangleIcon
                color="background"
                height="16"
                width="16"
              />
              <Text color="background" size="small" ml="8px">
                Video failed to load. Please try again.
              </Text>
            </Box>
          </CustomAbsolute>
        )}
        {isPlaying && (
          <Live>
            <Circle />
            <Text color="white" size="xsmall" ml="4px">
              LIVE
            </Text>
          </Live>
        )}
      </VideoContainer>
      <Box horizontal justify="space-between" align="center">
        <IconButton onClick={toggleMuted} disabled={!hasSound}>
          {isMuted ? (
            <AudioMuteIcon height="24px" width="24px" />
          ) : (
            <AudioControlIcon height="24px" width="24px" />
          )}
        </IconButton>
        <IconButton onClick={togglePlaying} height="40px" color="primaryText">
          {!isPlaying ? (
            <CirclePlayIcon width="40px" height="40px" />
          ) : (
            <CirclePauseIcon width="40px" height="40px" />
          )}
        </IconButton>
        <IconButton onClick={toggleFullscreen}>
          <FullscreenIcon height="24px" width="24px" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CustomVideo;
